import { useEffect } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { clearSentryUser, setSentryUser } from '@/lib/sentry';

export function useAnalyticsIdentity(user: User | null, session: Session | null): void {
  useEffect(() => {
    const activeUser = session?.user ?? user;
    let cancelled = false;

    const syncIdentity = async () => {
      if (cancelled) return;

      if (activeUser) {
        setSentryUser(
          activeUser.id,
          activeUser.email,
          activeUser.app_metadata?.organization_id
        );

        try {
          const ph = (await import('posthog-js')).default;
          if (ph.__loaded) {
            ph.identify(activeUser.id, {
              email: activeUser.email,
              name: activeUser.user_metadata?.full_name,
              organization_id: activeUser.app_metadata?.organization_id,
            });
            const orgId = activeUser.app_metadata?.organization_id;
            if (orgId) {
              ph.group('organization', orgId);
            }
          }
        } catch {
          // PostHog may not be initialized in all environments.
        }

        return;
      }

      clearSentryUser();
      try {
        const ph = (await import('posthog-js')).default;
        if (ph.__loaded) {
          ph.reset();
        }
      } catch {
        // PostHog may not be initialized in all environments.
      }
    };

    void syncIdentity();

    return () => {
      cancelled = true;
    };
  }, [user, session]);
}
